<template>
	<main>
		<InformationBar v-if="informationBar" :blok="informationBar" />

		<div ref="leafletContainer" class="space-y-section">
			<Breadcrumbs :items="breadcrumbs" class="container [&_a]:text-body-tertiary" />
			<div v-if="status === 'pending' && !leaflet" class="text-center py-5">
				<UiLoading :is-loading="true" class="p-2 text-2xl md:text-3xl lg:text-4xl inline-block" />
			</div>
			<section v-else-if="leaflet" class="container" aria-label="Akční leták">
				<div v-if="!selectedHypermarket" class="sm:mb-10">
					<h1 class="typo-h1 text-heading-primary mb-2">
						Akční leták
					</h1>
					<p class="mb-8">
						Výběrem místa zobrazíte všechny aktuálně platné i nadcházející letáky a katalogy dané pobočky.
					</p>
					<HypermarketSelectionBanner hide-header-icon show-links />
				</div>
				<div class="grid lg:grid-cols-[26.875rem_1fr] gap-4">
					<div class="flex flex-col">
						<div class="flex-auto">
							<div>
								<div class="lg:sticky lg:top-[3.7rem] xl:top-[4rem] bg-white py-4 lg:py-8 xl:py-10 z-10 flex justify-between gap-4">
									<LeafletHeader :leaflet="leaflet" />
									<Click
										:placement="AdPosition.ACTION_OFFER_LEAFLET_FIRST"
										class="max-sm:hidden lg:hidden"
									/>
								</div>
							</div>
							<LeafletAdSpaces :ad-spaces="adSpaces" />
						</div>
						<div class="sticky bottom-4 pt-6 max-lg:hidden">
							<LeafletNavPagination
								v-if="leaflet?.storeDocuments?.length"
								:visible="visibleMiniatures"
								:is-loading="!scrollTriggerIsLoading && !imagesIsLoaded"
								aria-label="Navigace letákem"
								@toggle-miniatures="toggleMiniatures"
							>
								<Transition name="fade">
									<div v-if="scrollTriggerIsLoading" class="px-2">
										{{ activeLeafletPage }} z {{ leafletsTotalNumberOfPages }}
									</div>
								</Transition>
							</LeafletNavPagination>

							<Transition name="fade">
								<LeafletNavPreviews
									v-if="visibleMiniatures && leaflet?.storeDocuments?.[0]?.jpgAssets?.length"
									:current-index="activeLeafletPage"
									:jpg-assets="leaflet?.storeDocuments?.[0]?.jpgAssets"
									@update:index="scrollToActiveLeafletPage($event)"
								/>
							</Transition>
						</div>
					</div>
					<div
						v-if="leafletPages.length"
						ref="leaflets"
						class="flex-auto space-y-2 relative"
					>
						<div
							v-if="!isActionOfferHistory && hasInteractiveMetadata"
							class="bg-background-noticeable lg:absolute lg:left-0 lg:right-0 lg:top-8 xl:top-16 mx-auto z-10 max-w-fit rounded-xl px-4 py-2 max-lg:mb-4"
						>
							<strong class="font-bold text-body-important">Interaktivní leták</strong> – klikněte na zvýrazněné položky
						</div>
						<div
							v-for="(page, index) in leafletPages"
							:id="`strana-${index + 1}`"
							:key="index"
							ref="viewportElements"
							class="block"
							:class="loadedLeafletsCount <= index && 'skeleton-gradient'"
						>
							<LeafletPage
								:index="index"
								:interactive="!isActionOfferHistory"
								:available-processed-images="page.hasOptimized"
								:images="page.image"
								:metadata="page.metadata"
								:action-offer-name="leaflet?.actionOfferName"
								:valid-from="leaflet?.validFrom"
								:valid-to="leaflet?.validTo"
								@image-loaded="handleLoadEvent"
								@metadata-loaded="(loaded) => handleMetadataLoaded(index, loaded)"
							/>
						</div>
						<Click
							:placement="AdPosition.ACTION_OFFER_LEAFLET_SECOND"
							class="sm:hidden my-2"
						/>
						<Click
							:placement="AdPosition.ACTION_OFFER_LEAFLET_FIRST"
							class="max-md:hidden lg:hidden"
							:custom-asset="{width: 820, height: 200, asset: 'mobile'}"
						/>
						<Click
							:placement="AdPosition.ACTION_OFFER_LEAFLET_FIRST"
							class="max-sm:hidden md:hidden"
							:custom-asset="{width: 450, height: 130, asset: 'tablet'}"
						/>

						<ClientTeleport>
							<Transition name="fade">
								<ScrollToTop v-if="leafletsIsVisible && scrollY > leafletsHeight && isDesktop">
									Zpět na začátek
								</ScrollToTop>
							</Transition>
						</ClientTeleport>
					</div>
				</div>
			</section>
			<div v-else class="container text-center pt-16">
				<h1 class="typo-h1 text-heading-primary mb-4">
					{{ leafletNotFoundHeading || 'Leták již není dostupný' }}
				</h1>
				<NuxtLink
					:to="{name: 'branch-letaky', params: {branch: hypermarketSlug}}"
					class="btn btn-sm md:btn-lg btn-secondary shadow-sm"
				>
					<NuxtIcon name="custom/leaflet" filled class="text-xl" />
					Zobrazit aktuální letáky
				</NuxtLink>
			</div>
			<LeafletListing />
			<ActionOfferProductListing :title="`Akční nabídka ${hypermarket?.content.name}`" />
		</div>
	</main>
</template>

<script setup lang="ts">
	import {gsap} from 'gsap';
	import {ScrollTrigger} from 'gsap/ScrollTrigger';
	import {useElementBounding, useElementVisibility, useWindowScroll, watchDebounced, useDebounceFn, useToggle} from '@vueuse/core';
	import {useRouteHash} from '@vueuse/router';
	import {storeToRefs} from 'pinia';
	import type {ImageSet} from '@globus/image-overlay';
	import {AdPosition} from '~/openapi';
	import type {BreadcrumbItem} from '@/types/breadcrumbs';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import useTailwindBreakpoints from '~/composables/useTailwindBreakpoints';
	import useLifecycleCallbacks from '~/composables/useLifecycleCallbacks';
	import {useLeafletTracking} from '~/composables/useLeafletTracking';
	import {useLeafletLayout} from '~/composables/useLeafletLayout';
	import {useLeafletScrollTriggers} from '~/composables/useLeafletScrollTriggers';
	import {
		provideLeafletProductNavigation,
		useLeafletProductNavigation,
	} from '~/composables/useLeafletProductNavigation';
	import {getLeafletGTMPageType} from '~/utils/leaflets';
	import {computerToHumanIndexing, humanToComputerIndexing} from '~/utils';
	import scrollToSection from '~/utils/scrollToSection';
	import type {Gsoa_ActionOffers_ActionOffer as ActionOffer} from '~/openapi';

	gsap.registerPlugin(ScrollTrigger);

	type Props = {
		leaflet: ActionOffer | null;
		status?: string;
		leafletNotFoundHeading?: string;
		isActionOfferHistory?: boolean;
	};

	const props = withDefaults(defineProps<Props>(), {isActionOfferHistory: false, status: 'success'});
	const leaflet = toRef(props, 'leaflet');

	const DEBOUNCE_DELAY_MS = 100;
	const DEBOUNCE_MAX_WAIT_MS = 1000;

	const route = useRoute();
	const hash = useRouteHash();
	const branch = useRouteParams<string>('branch');
	const leafletMetadataBaseUrl = String(useRuntimeConfig().public.leafletMetadataBaseUrl);

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketInstance, hypermarketsBySlug} = storeToRefs(hypermarketsStore);

	const hypermarket = computed(() => hypermarketsBySlug.value[branch.value]);
	const hypermarketSlug = computed(() => hypermarket.value?.slug ?? 'globus');

	const leaflets = ref<HTMLElement | null>(null);
	const leafletsTotalNumberOfPages = computed(() => leaflet.value?.storeDocuments?.[0]?.jpgAssets?.length ?? 0);
	const leafletsIsVisible = useElementVisibility(leaflets);
	const breakpoints = useTailwindBreakpoints();
	const isDesktop = breakpoints.greater('md');
	const branchSpecificPath = useBranchSpecificPath();

	const breadcrumbs = computed<BreadcrumbItem[]>(() => [
		{
			id: 0,
			parent_id: null,
			slug: hypermarketSlug.value ?? 'globus',
			name: hypermarket.value?.content.name || 'Globus',
		},
		{
			id: 1,
			parent_id: 0,
			slug: branchSpecificPath('/letaky').value,
			name: 'Letáky',
		},
		{
			id: 2,
			parent_id: 1,
			slug: route.path,
			name: 'Akční leták',
		},
	]);
	const informationBar = computed(() => {
		const bar = selectedHypermarketInstance.value?.content.informationBar![0];
		return bar && bar.visibleOnPages?.includes('PageTypeLeafletDetail') ? bar : null;
	});

	const milestones = computed(() => {
		const totalPages = leafletsTotalNumberOfPages.value;
		const increment = totalPages <= 12 ? 2 : 4;
		return Array.from({length: Math.ceil(totalPages / increment)}, (_, i) => (i + 1) * increment);
	});

	const jpgAssets = computed(() => {
		const processed = leaflet.value?.storeDocuments?.[0]?.jpgAssetsProcessed;

		if (Array.isArray(processed) && processed.length > 0) {
			return processed;
		}

		return leaflet.value?.storeDocuments?.[0]?.jpgAssets;
	});

	function getAssetIdFromUrl(urlString: string): string | null {
		try {
			return new URL(urlString).searchParams.get('assetID');
		} catch {
			return null;
		}
	}

	function getPathHashFromUrl(urlString: string): string | undefined {
		try {
			return new URL(urlString).pathname.split('/').filter(Boolean).pop()?.replace(/-\d+$/, '');
		} catch {
			return undefined;
		}
	}

	function getMetadataIdFromUrl(urlString: string): string | undefined {
		return getAssetIdFromUrl(urlString) ?? getPathHashFromUrl(urlString);
	}

	const leafletPages = computed(() => {
		const baseUrl = leafletMetadataBaseUrl.replace(/\/$/, '');

		if (!Array.isArray(jpgAssets.value)) {
			return [];
		}

		return jpgAssets.value.map((item) => {
			let image: ImageSet;
			let hasOptimized = false;
			const standardOriginal = typeof item === 'string' ? item : (item.original ?? '');

			if (typeof item === 'string') {
				image = {original: item};
			} else {
				image = {original: standardOriginal};
				hasOptimized = item.optimized != null;

				if (item.optimized) {
					Object.assign(image, item.optimized);
				}
			}

			// Prefer optimized URL (assetID → path hash); otherwise same from GSOA original.
			const metadataSourceUrl = hasOptimized ? image.original : standardOriginal;
			const metadataId = getMetadataIdFromUrl(metadataSourceUrl);

			return {
				image,
				metadata: {original: metadataId ? `${baseUrl}/${metadataId}.json` : ''},
				hasOptimized,
			};
		});
	});

	const imagesIsLoaded = ref(false);
	const pagesWithInteractiveMetadata = ref(new Set<number>());
	const hasInteractiveMetadata = computed(() => pagesWithInteractiveMetadata.value.size > 0);
	const activeLeafletPage = ref(1);

	const [visibleMiniatures, toggleMiniatures] = useToggle();
	const viewportElements = ref<HTMLElement[]>([]);
	const loadedLeafletsCount = ref(0);
	const scrollTriggerIsLoading = ref(false);

	const trackedProgress = ref<number[]>([]);
	const {y: scrollY} = useWindowScroll();
	const {height: leafletsHeight} = useElementBounding(leaflets);

	const leafletLength = computed(() => leaflet.value?.storeDocuments?.[0]?.jpgAssets?.length ?? 0);
	const baseHeight = computed(() => viewportElements.value[0]?.offsetHeight ?? 0);

	const {adSpaces} = useLeafletLayout(leafletLength, baseHeight);

	const gtmPageType = computed(() => getLeafletGTMPageType(leaflet.value));
	const {trackPageView, trackScrollProgress, trackPageShowtime} = useLeafletTracking(leaflet, gtmPageType);

	const {setupPageScrollTriggers, setupListVisibilityTriggers} = useLeafletScrollTriggers({
		viewportElements,
		activeLeafletPage,
		scrollTriggerIsLoading,
		leafletsTotalNumberOfPages,
		leaflet,
		trackPageShowtime,
		computerToHumanIndexing,
		humanToComputerIndexing,
	});

	const scrollToActiveLeafletPage = useDebounceFn((index: number) => {
		activeLeafletPage.value = index;
		scrollToSection(`#strana-${index}`);
	}, DEBOUNCE_DELAY_MS, {maxWait: DEBOUNCE_MAX_WAIT_MS});

	const productNavigation = useLeafletProductNavigation(leafletPages, {onPageChange: scrollToActiveLeafletPage});

	if (!props.isActionOfferHistory) {
		provideLeafletProductNavigation(productNavigation);
	}

	function handleLoadEvent() {
		loadedLeafletsCount.value++;

		if (leafletsTotalNumberOfPages.value === loadedLeafletsCount.value) {
			imagesIsLoaded.value = true;
		}
	}

	function handleMetadataLoaded(pageIndex: number, loaded = true) {
		if (!loaded || pagesWithInteractiveMetadata.value.has(pageIndex)) {
			return;
		}

		pagesWithInteractiveMetadata.value = new Set([...pagesWithInteractiveMetadata.value, pageIndex]);
	}

	watch(() => leaflet.value?.actionOfferId, () => {
		pagesWithInteractiveMetadata.value = new Set();
	});

	onMounted(() => {
		trackPageView(leafletsTotalNumberOfPages.value);
	});

	watch(activeLeafletPage, (newValue, oldValue) => {
		if (newValue > oldValue && milestones.value.includes(newValue) && !trackedProgress.value.includes(newValue) && (newValue - oldValue) <= 2) {
			const progress = Math.round((newValue / leafletsTotalNumberOfPages.value) * 100);
			trackScrollProgress(progress, newValue, leafletsTotalNumberOfPages.value, leaflet.value?.storeDocuments?.[0]?.jpgAssets?.[humanToComputerIndexing(newValue)]);
			trackedProgress.value.push(newValue);
		}
	});

	watch(loadedLeafletsCount, (newValue) => {
		const match = hash.value?.match(/strana-(\d+)/);
		const page = match ? Number(match[1]) : null;
		if (page && newValue === page) {
			scrollToSection(hash.value);
		}
	});

	useSelectedHypermarketRouteSync(branch);

	watchDebounced(
		loadedLeafletsCount,
		() => { ScrollTrigger.refresh(); },
		{debounce: 500},
	);

	useLifecycleCallbacks(() => {
		const visibilityTriggers = setupListVisibilityTriggers();
		const triggers = setupPageScrollTriggers();

		return () => {
			visibilityTriggers.forEach((cleanup) => cleanup());
			triggers.forEach((trigger) => trigger.kill());

			trackedProgress.value = [];
		};
	});
</script>
